import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isEqual, unescape } from "../../utils/lodash";
import useStyles from "./Select.styles";
import { RecalledText } from "../../containers/shared/RecalledText";
import { isNullOrEmpty, removeHtmlTags } from "../../utils";
import InputWrapper from "../shared/InputWrapper";

export interface Option {
    id: string;
    label: string;
    value: string;
    recalledLabel: string;
}

interface Props {
    id: string;
    labelId: string;
    openEmpty?: boolean;
    required?: boolean;
    value: string;
    options: Option[];
    minLength?: number;
    maxResults?: number;
    maxResultsMessage?: string;
    searchMethod?: "Exact" | "StartsWith" | "Contains";
    onChange(value: string): void;
    onBlur();
    onOptionSelected?(value: string): void;
    size?: string;
}

export default function AutoComplete(props: Props) {
    const {
        options,
        minLength,
        maxResults,
        maxResultsMessage,
        searchMethod,
        required,
        id,
        labelId,
        value,
        onBlur,
        onChange,
        size,
        onOptionSelected,
    } = props;

    const ref = React.useRef<HTMLDivElement>();
    const [dropdownWidth, setDropdownWidth] = React.useState(0);
    const [focusedOption, setFocusedOption] = React.useState<Option>();
    const [focused, setFocused] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const theme = useTheme();
    const classes = useStyles({ theme, size, autoComplete: true });

    React.useLayoutEffect(() => {
        // Listen to scroll events to measure dropdown position.
        if (!expanded) {
            return undefined;
        }
        function measure() {
            const rect = ref.current.getBoundingClientRect();
            if (!isEqual(dropdownWidth, rect.width)) {
                setDropdownWidth(rect.width);
            }
        }
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return function cleanup() {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [expanded, dropdownWidth]);

    const selectedOption = options.find(
        (o) => value.trim().toLowerCase() === o.label.trim().toLowerCase(),
    );

    React.useEffect(() => {
        if (focusedOption) {
            document.getElementById(focusedOption.id).focus();
        }
    }, [focusedOption, expanded]);

    function focusOption(option) {
        document.getElementById(option.id).focus();
    }

    function selectOption(option) {
        onChange(option.label);
        if (onOptionSelected) {
            onOptionSelected(option.label);
        }
    }

    React.useEffect(() => {
        function clickOutside(event) {
            if (ref.current.contains(event.target)) {
                return;
            }
            setExpanded(false);
        }
        document.addEventListener("mousedown", clickOutside);
        return function cleanup() {
            document.removeEventListener("mousedown", clickOutside);
        };
    }, []);

    const filteredOptions = React.useMemo(() => {
        if (minLength > 0 && (isNullOrEmpty(value) || value.length < minLength)) {
            return [];
        }

        let result = options;
        const trimmedLowerValue = value.trim().toLowerCase();

        if (isNullOrEmpty(trimmedLowerValue)) {
            return result;
        }

        switch (searchMethod) {
            case "Exact":
                result = result.filter(
                    (c) => c.recalledLabel.trim().toLowerCase() === trimmedLowerValue,
                );
                break;
            case "StartsWith":
                result = result.filter((c) =>
                    c.recalledLabel.trim().toLowerCase().startsWith(trimmedLowerValue),
                );
                break;
            default:
                result = result.filter((c) =>
                    c.recalledLabel.trim().toLowerCase().includes(trimmedLowerValue),
                );
                break;
        }
        return result;
    }, [value, minLength, options, searchMethod]);

    const displayMaxResultsMessage = maxResults && filteredOptions.length > maxResults;

    const limitedFilteredOptions = filteredOptions.slice(0, maxResults || 50);

    function selectFirstOption() {
        const firstOption = limitedFilteredOptions[0];
        focusOption(firstOption);
    }

    function selectLastOption() {
        const lastOption = limitedFilteredOptions[limitedFilteredOptions.length - 1];
        focusOption(lastOption);
    }

    function selectPreviousOption() {
        const focusedIndex = limitedFilteredOptions.indexOf(focusedOption);
        const previousIndex = Math.max(0, focusedIndex - 1);
        const nextOption = limitedFilteredOptions[previousIndex];
        focusOption(nextOption);
    }

    function selectNextOption() {
        const focusedIndex = limitedFilteredOptions.indexOf(focusedOption);
        const nextIndex = Math.min(limitedFilteredOptions.length - 1, focusedIndex + 1);
        const nextOption = limitedFilteredOptions[nextIndex];
        focusOption(nextOption);
    }

    function closeListBox() {
        setExpanded(false);
    }

    function openListBox() {
        setExpanded(true);
    }

    React.useEffect(() => {
        if (!expanded && focused && value.length >= (minLength || 0)) {
            openListBox();
        }
    }, [value, minLength, expanded, focused]);

    function onButtonKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter") {
            openListBox();
            e.preventDefault();
        }
        if (e.key === "ArrowUp") {
            openListBox();
            selectPreviousOption();
            e.preventDefault();
        }
        if (e.key === "ArrowDown") {
            openListBox();
            selectNextOption();
            e.preventDefault();
        }
    }

    function onOptionKeyDown(option: Option, e: React.KeyboardEvent) {
        if (e.key === "Enter") {
            selectOption(option);
            closeListBox();
            e.preventDefault();
        }
        if (e.key === "Escape") {
            closeListBox();
            e.preventDefault();
        }
        if (e.key === "ArrowUp") {
            selectPreviousOption();
            e.preventDefault();
        }
        if (e.key === "ArrowDown") {
            selectNextOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Home") {
            selectFirstOption();
            e.preventDefault();
        }
        if (e.key === "End") {
            selectLastOption();
            e.preventDefault();
        }
    }

    return (
        <InputWrapper size={size}>
            <div
                ref={ref}
                className={classnames(
                    classes.select,
                    focused && classes.selectFocused,
                    expanded && classes.selectExpanded,
                )}
                onBlur={(e) => {
                    const target = e.relatedTarget || document.activeElement;
                    // If the element that *gained* focus is not contained within, close the list box.
                    if (!ref.current.contains(target as Node)) {
                        closeListBox();
                        onBlur();
                    }
                }}
            >
                <input
                    id={id}
                    aria-labelledby={labelId}
                    tabIndex={0}
                    type="text"
                    className={classes.selectContent}
                    onKeyDown={onButtonKeyDown}
                    onBlur={() => setFocused(false)}
                    onFocus={() => {
                        if (!minLength) {
                            openListBox();
                        }
                        setFocused(true);
                    }}
                    value={unescape(value)}
                    autoComplete="off"
                    onChange={(e) => onChange(e.target.value)}
                />
                <FontAwesomeIcon
                    icon={expanded ? "caret-up" : "caret-down"}
                    className={classnames(classes.icon, classes.iconCaret)}
                />
                {/* eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex */}
                <ul
                    role="listbox"
                    className={classnames(
                        classes.listbox,
                        classes.dropDown,
                        !expanded && classes.hidden,
                        expanded ? classes.listboxExpanded : "",
                    )}
                    style={{ minWidth: dropdownWidth }}
                    tabIndex={-1}
                    aria-required={required}
                    aria-activedescendant={selectedOption && selectedOption.id}
                >
                    {limitedFilteredOptions.map((option) => (
                        <li
                            id={option.id}
                            key={option.id}
                            role="option"
                            onClick={(e) => {
                                selectOption(option);
                                closeListBox();
                                e.preventDefault();
                            }}
                            onFocus={() => setFocusedOption(option)}
                            onBlur={() => setFocusedOption(undefined)}
                            onKeyDown={(e) => onOptionKeyDown(option, e)}
                            className={classes.option}
                            aria-label={removeHtmlTags(option.label)}
                            tabIndex={-1}
                            aria-selected={selectedOption === option}
                        >
                            <RecalledText text={option.label} ariaHidden={true} />
                        </li>
                    ))}
                    {displayMaxResultsMessage && (
                        <li className={classes.message} tabIndex={-1}>
                            <RecalledText
                                text={maxResultsMessage.replace(/{max}/, maxResults.toString())}
                            />
                        </li>
                    )}
                </ul>
            </div>
        </InputWrapper>
    );
}
